import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Button,
    Divider,
    IconButton,
    Popper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useDataContext } from '../Context/useDataContext';
import { useLSContext } from '../Context/useLSContext';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../Settings/appSettings';
import { useHoleSettings } from '../Context/useHoleSettingsContext';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    feetAndInchesToDecimalFeet,
    formatDecimalFeetAsFeetInches,
    isFeetInchesNumberChallenge,
    normalizeFeetAndInches,
    roundToDecimalPlaces,
} from '@moment-golf/shared';

interface PlayerOption {
    label: string;
    value: number;
}

interface challengeSubmission {
    challengeName: string,
    challengeHole: number,
    playerName: string,
    value: any,
    submittedAt: Date,
    submittedBy: string
}

interface ChallengesProps {
    challenge: any;
}

export const Challenges = ({ challenge }: ChallengesProps) => {

    const { registrationData, eventData, scoreData, currentRound, scoreCode, setScoreData } = useDataContext();
    const { appTheme } = useLSContext();
    const {holeSettings} = useHoleSettings();

    const auth = useAuth();
    const api = CreateApi();

    const [selectedPlayer, setSelectedPlayer] = useState<PlayerOption | null>(null);
    const [challengeScore, setChallengeScore] = useState<number>(0);
    const [challengeFeet, setChallengeFeet] = useState<number>(0);
    const [challengeInches, setChallengeInches] = useState<number>(0);

    const useFeetInches = isFeetInchesNumberChallenge(challenge ?? {});
    const decimalPlaces = useMemo(() => {
        const p = Number(challenge?.decimalPlaces);
        return Number.isFinite(p) ? p : 1;
    }, [challenge?.decimalPlaces]);

    /** Prefer prop; fall back to same hole/name on the active round (some payloads omit `units`). */
    const challengeUnits = useMemo(() => {
        const direct = String(challenge?.units ?? '').trim();
        if (direct) return direct;
        const round = eventData?.rounds?.find((r: any) => r.roundNumber === currentRound);
        const match = round?.challenges?.find(
            (c: any) =>
                c.hole === challenge?.hole &&
                (c.name === challenge?.name || c.type === challenge?.name || c.type === challenge?.type),
        );
        return String(match?.units ?? '').trim();
    }, [challenge?.units, challenge?.hole, challenge?.name, challenge?.type, eventData?.rounds, currentRound]);

    const playerOptions: PlayerOption[] = useMemo(() => registrationData.map((player: any, index: number) => ({
        label: player.playerName,
        value: index
    })).sort((a: PlayerOption, b: PlayerOption) => a.label.localeCompare(b.label)),
     [registrationData]);

    useEffect(() => {
        setSelectedPlayer(null);
        setChallengeScore(0);
        setChallengeFeet(0);
        setChallengeInches(0);
    }, [holeSettings]);

    const submitChallenge = () => {

        // console.log('selectedPlayer', selectedPlayer);
        // console.log('challengeScore', challengeScore);

        const playerRegistration = registrationData.find((player: any) => player.playerName === selectedPlayer?.label);

        // console.log('playerRegistration', playerRegistration);
        // console.log('scoreData', scoreData);

        const add2ScoreData = scoreData.find((score: any) => 
            score.roundScores[currentRound-1].markerScoreCode === scoreCode
        );

/*         const scoreDataCopy = JSON.parse(JSON.stringify(scoreData));
        const add2ScoreData = scoreDataCopy.filter((score: any) => {
            return score.roundScores.some((roundScore: any) => 
                roundScore.roundNumber === currentRound &&
                roundScore.markerScoreCode === scoreCode &&
                roundScore.playerScoreCode !== scoreCode
            );
        }); */

        // console.log('add2ScoreData', add2ScoreData);

        const updatedChallenges = add2ScoreData.roundScores
            .filter((roundScore: any) => 
                roundScore.roundNumber === currentRound
            )
            .flatMap((roundScore: any) => roundScore.challengeSubmissions || []);

        // console.log('updatedChallenges', updatedChallenges);

        const rawNumber = useFeetInches
            ? feetAndInchesToDecimalFeet(challengeFeet, challengeInches)
            : challengeScore;
        const numericValue =
            challenge.inputType === 'number' ? roundToDecimalPlaces(rawNumber, decimalPlaces) : true;

        const newSubmission: challengeSubmission = {
            challengeName: challenge.name,
            challengeHole: challenge.hole,
            playerName: selectedPlayer?.label || '',
            value: numericValue,
            submittedAt: new Date(),
            submittedBy: auth.user?.profile.sub || scoreCode
        };

        if (updatedChallenges.find((submission: any) => submission.challengeName === challenge.name && submission.challengeHole === challenge.hole && submission.playerName === selectedPlayer?.label)) {
            updatedChallenges.forEach((submission: any, index: number) => {
                if (submission.challengeName === challenge.name &&
                     submission.challengeHole === challenge.hole &&
                      submission.playerName === selectedPlayer?.label) {
                    updatedChallenges[index] = newSubmission;
                }
            });
        } else {
            updatedChallenges.push(newSubmission);
        }

        // console.log('updatedChallenges', updatedChallenges);

        // Find the correct round in roundScores using findIndex
        const roundIndex = add2ScoreData.roundScores.findIndex(
            (roundScore: any) => roundScore.roundNumber === currentRound
        );

        // Only update if the round exists
        if (roundIndex !== -1) {
            add2ScoreData.roundScores[roundIndex].challengeSubmissions = updatedChallenges;
        } else {
            console.error('Round not found:', currentRound);
        }

        const updatedScoreData = scoreData.map((score: any) => {

            if (score._id === add2ScoreData._id) {
                return add2ScoreData;
            }
            return score;
        });

        setScoreData(updatedScoreData);

        api.post('/ls/UpdateChallengeScores', {
            eventScoreID: add2ScoreData._id,
            roundIndex: roundIndex,
            challengeSubmissions: updatedChallenges,
            userID: auth.user?.profile.sub || `scoreCode:${scoreCode}`
        });

    }

    const deleteChallengeSubmission = (submission: any) => {
        // console.log('Deleting Challenge Submission', submission);

        let updatedChallenges = [];

        const add2ScoreData = scoreData.find((score: any) => 
            score.roundScores[currentRound-1].markerScoreCode === scoreCode
        );

        


        // Find the correct round in roundScores using findIndex
        const roundIndex = add2ScoreData.roundScores.findIndex(
            (roundScore: any) => roundScore.roundNumber === currentRound
        );

       

        // Only update if the round exists
        if (roundIndex !== -1) {
            updatedChallenges = add2ScoreData.roundScores[roundIndex].challengeSubmissions
            .filter((challenge: any) => JSON.stringify(challenge) !== JSON.stringify(submission));
            add2ScoreData.roundScores[roundIndex].challengeSubmissions = updatedChallenges;
        } else {
            console.error('Round not found:', currentRound);
        }

        const updatedScoreData = scoreData.map((score: any) => {

            if (score._id === add2ScoreData._id) {
                return add2ScoreData;
            }
            return score;
        });

        setScoreData(updatedScoreData);

        api.post('/ls/UpdateChallengeScores', {
            eventScoreID: add2ScoreData._id,
            roundIndex: roundIndex,
            challengeSubmissions: updatedChallenges,
            userID: auth.user?.profile.sub || `scoreCode:${scoreCode}`
        });

    }

    return (
        challenge?.name &&
        <Stack direction="column" spacing={1}
            sx={{ width: '100%', alignItems: 'center', justifyContent: 'center'}}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500}}>{challenge.name}</Typography>
            <Stack direction="row" spacing={1} width="90%" alignItems="center" justifyContent="center">
                <Autocomplete
                    options={playerOptions}
                    getOptionLabel={(option) => option?.label || ''}
                value={selectedPlayer}
                onChange={(event, value) => setSelectedPlayer(value)}
                sx={{
                    '& .MuiAutocomplete-root': {
                        '& .MuiAutocomplete-paper .MuiAutocomplete-option:focus': {
                            borderColor: appTheme.palette.primary.main, // Keep the border green when focused
                        },
                    },
                    width: 180,
                }}
                renderInput={(params) => <TextField 
                    {...params} 
                    label="Select Player" 
                    variant="outlined" 
                    error={!selectedPlayer}
                    InputProps={{
                        ...params.InputProps,
                        readOnly: true,
                    }}
                    sx={{
                        padding: 0,
                        "& .MuiInputBase-input": { fontSize: 16, height: 14, padding: 1 },
                        '& .MuiOutlinedInput-root': { 
                            padding: '5px 10px',
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: appTheme.palette.primary.main, // Keep the border green when focused
                            },  
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: appTheme.palette.primary.main, // Always green outline
                            },
                        },
                        '& input': { textAlign: 'left', lineHeight: 'normal' },
                    }}
                    inputProps={{
                        ...params.inputProps,
                        autoComplete: "off",
                        readOnly: true,
                        inputMode: "none",
                        sx: {
                            fontSize: 16,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: appTheme.palette.primary.main,
                            },
                        },
                    }}
                    InputLabelProps={{
                        sx: { fontSize: 14, lineHeight: 'normal', padding: '0px 10px', backgroundColor: 'white', marginTop: '0px' },
                        shrink: true
                    }}
                    
                />}
                PopperComponent={(props) => (
                    <Popper {...props} autoFocus sx={{ 
                        zIndex: 1300, // Ensure it appears above other elements
                        '& .MuiAutocomplete-listbox': {
                            maxHeight: 200,
                            overflowY: 'auto',
                            bgcolor: 'whitesmoke',
                        },
                        '& .MuiAutocomplete-option': {
                            padding: '0px 10px',
                            '&:hover': {
                                backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                borderRadius: 3
                            },
                            fontSize: 16,
                            maxHeight: 20,
                        },
                        '& .Mui-focusVisible' : {
                                backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                borderRadius: 3,
                        },
                        
                    }} />
                )}
            />
            {challenge.inputType === 'number' && !useFeetInches && (
            <TextField
                    id='challenge-score'
                    type="number"
                    value={challengeScore}
                    onChange={(e) => {
                        setChallengeScore(Number(e.target.value));
                    }}
                    onFocus={(e) => {
                        e.target.select();
                    }}
                    sx={{ 
                        width: 80,
                        height: 40,
                        padding: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white'
                    }}
                    InputLabelProps={{
                        sx: { fontSize: 16 }
                    }}
                    inputProps={{
                        inputMode: 'decimal',
                        pattern: '[0-9]*[.,]?[0-9]*',
                        style: { 
                            textAlign: 'center',
                            padding: 0,
                            fontSize: 16,
                            fontWeight: 500,
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 80,
                            height: 40,
                        }
                    }}
                />
            )}
            {challenge.inputType === 'number' && useFeetInches && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <TextField
                        id="challenge-feet"
                        type="number"
                        label="Ft"
                        value={challengeFeet}
                        onChange={(e) => setChallengeFeet(Number(e.target.value))}
                        onBlur={() => {
                            const n = normalizeFeetAndInches(challengeFeet, challengeInches);
                            setChallengeFeet(n.feet);
                            setChallengeInches(n.inches);
                        }}
                        onFocus={(e) => e.target.select()}
                        sx={{ width: 72 }}
                        inputProps={{
                            inputMode: 'numeric',
                            min: 0,
                            style: { textAlign: 'center', fontSize: 16, fontWeight: 500 },
                        }}
                        InputLabelProps={{ shrink: true, sx: { fontSize: 14 } }}
                    />
                    <TextField
                        id="challenge-inches"
                        type="number"
                        label="In"
                        value={challengeInches}
                        onChange={(e) => setChallengeInches(Number(e.target.value))}
                        onBlur={() => {
                            const n = normalizeFeetAndInches(challengeFeet, challengeInches);
                            setChallengeFeet(n.feet);
                            setChallengeInches(n.inches);
                        }}
                        onFocus={(e) => e.target.select()}
                        sx={{ width: 72 }}
                        inputProps={{
                            inputMode: 'decimal',
                            min: 0,
                            style: { textAlign: 'center', fontSize: 16, fontWeight: 500 },
                        }}
                        InputLabelProps={{ shrink: true, sx: { fontSize: 14 } }}
                    />
                </Stack>
            )}
            {!useFeetInches && challengeUnits ? (
                <Typography variant="body1">{challengeUnits}</Typography>
            ) : null}
            </Stack>
            <Button variant="contained" color="primary" 
            sx={{ width: '80%'}}
            onClick={() => {
                submitChallenge();
            }}>Submit</Button>
            <Divider sx={{ width: '90%'}} />
        <Stack direction="column" spacing={1} width="90%" alignItems="center" justifyContent="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 500}}>Challenge Submissions</Typography>
            {scoreData.flatMap((score: any) => {
                const roundScore = score.roundScores.find((r: any) => r.roundNumber === currentRound);
                const subs = roundScore?.challengeSubmissions?.filter(
                    (submission: any) =>
                        submission.challengeName === challenge.name &&
                        Number(submission.challengeHole) === Number(challenge.hole),
                ) ?? [];
                return subs.map((submission: any) => {
                    const numVal = Number(submission.value);
                    const showPrime =
                        useFeetInches && Number.isFinite(numVal);
                    const displayValue = showPrime
                        ? formatDecimalFeetAsFeetInches(numVal)
                        : submission.value;
                    return (
                        <Stack direction="row" spacing={0.5} width="100%" alignItems="center" justifyContent="center"
                            key={`${score._id}-${submission.playerName}-${String(submission.value)}`}
                        >
                            <Stack direction="row" spacing={0.5} flex={3} alignItems="center" justifyContent="flex-end">
                                <Typography variant="body1">{submission.playerName}</Typography>
                                <Typography variant="body1">
                                    {displayValue}
                                    {!showPrime && challengeUnits ? ` ${challengeUnits}` : ''}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} flex={2} alignItems="center" justifyContent="flex-start">
                                <IconButton onClick={() => deleteChallengeSubmission(submission)}>
                                    <DeleteOutlineIcon color="error"/>
                                </IconButton>
                            </Stack>
                        </Stack>
                    );
                });
            })}
        </Stack>
        </Stack>
    );
};